import type { ApexOptions } from 'apexcharts';
import './OutdoorAirStatictic.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { type ColumnDefinition, ReactTabulator } from 'react-tabulator';
import type { CellComponent, Tabulator } from 'tabulator-tables';
import { URLS } from '@/shared/config/urls';
import { useColorStore } from '@/store/colorStore';
import { useThemeStore } from '@/store/themeStore';
import type { AirMeasurementMetrics, AirQualityStatisticResponse } from '../types/air-stats.types';
import { lttbGapAware } from '../utils/lttb';

const SENSOR_TYPE = 'AIR_TP_IN';
const TOTAL_ID = 'tabulator-indoor-total-count';

const buildUrl = (params: string) => {
  const fullParams = params ? `sensor_type=${SENSOR_TYPE}&${params}` : `sensor_type=${SENSOR_TYPE}`;
  return `${URLS.BACK_DSH}/air-stats?${fullParams}`;
};

const nullFormatter = (cell: CellComponent) => {
  const value = cell.getValue();
  return value === null || value === undefined ? '-' : value;
};

const IndoorAirTabulator: ColumnDefinition[] = [
  {
    title: 'No',
    formatter: (cell: CellComponent) => {
      const row = cell.getRow();
      const page = row.getTable().getPage();
      const pageSize = row.getTable().getPageSize();
      return (page - 1) * pageSize + row.getPosition(true);
    },
    width: 60,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '집계 시간',
    field: 'aggregated_time',
    hozAlign: 'center',
    widthGrow: 2,
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '사이트',
    field: 'site_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '센서명',
    field: 'sensor_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '평균<br>미세먼지(ug/m³)',
    field: 'pm10',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
  {
    title: '평균<br>초미세먼지(ug/m³)',
    field: 'pm25',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
  {
    title: '평균<br>온도(℃)',
    field: 'temp',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
  {
    title: '평균<br>습도(%)',
    field: 'humi',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
  {
    title: '평균<br>이산화탄소(ppm)',
    field: 'co2',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
  {
    title: '평균<br>휘발성유기화합물(ppb)',
    field: 'vocs',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
  {
    title: '평균<br>일산화탄소(ppm)',
    field: 'co',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
  {
    title: '평균<br>포름알데히드(ppm)',
    field: 'hcho',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: nullFormatter,
  },
];

const toSeries = (points?: { timestamp: string; value: number | null }[]) =>
  points?.map((p) => ({ x: new Date(p.timestamp).getTime(), y: p.value })) ?? [];

// Parse the committed range from queryParams so all charts in a tab share an x-axis.
const parseRange = (queryParams: string) => {
  const params = new URLSearchParams(queryParams);
  const start = params.get('start_time');
  const end = params.get('end_time');
  return {
    min: start ? new Date(start).getTime() : undefined,
    max: end ? new Date(end).getTime() : undefined,
  };
};

const IndoorAirStatictic = ({
  metrics,
  queryParams,
}: {
  metrics?: AirMeasurementMetrics;
  queryParams: string;
}) => {
  const { t } = useTranslation();
  const { apexChart: apexColors, NEWCOLOR } = useColorStore();
  const isDark = useThemeStore((state) => state.isDark);
  const tbRef = useRef<Tabulator | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const handleAjaxResponse = useCallback(
    (_url: string, _params: unknown, response: AirQualityStatisticResponse) => {
      const total = response?.data?.total ?? 0;
      setTotalCount(total);
      return {
        data: response?.data?.rows ?? [],
        last: response?.data?.total_pages ?? 1,
        total,
      };
    },
    []
  );

  useEffect(() => {
    const el = document.getElementById(TOTAL_ID);
    if (el) el.textContent = `총 ${totalCount} 건`;
  }, [totalCount]);

  useEffect(() => {
    if (!tbRef.current) return;
    tbRef.current.setData(buildUrl(queryParams));
  }, [queryParams]);

  const languageTabulator = useCallback(
    () => ({
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    }),
    [t]
  );

  const optionsTabulator = useMemo(
    () => ({
      pagination: true,
      paginationMode: 'remote',
      paginationSize: 10,
      rowHeight: 41,
      movableRows: false,
      index: 'aggregated_time',
      locale: 'ko',
      langs: { ko: languageTabulator() },
      resizableRows: false,
      footerElement: `<div id="${TOTAL_ID}" style="padding: 0 20px 0 0; text-align: right;">총 ${totalCount} 건</div>`,
      placeholder: "<div style='text-align:center;'> No Data</div>",
      ajaxURL: buildUrl(queryParams),
      ajaxURLGenerator: (
        url: string,
        _config: unknown,
        params: { page?: number; size?: number }
      ) => {
        if (params.page) {
          return `${url}&page=${params.page}&size=${params.size || 10}`;
        }
        return url;
      },
      ajaxConfig: {
        method: 'GET',
        credentials: 'include',
      },
      ajaxResponse: handleAjaxResponse,
      dataReceiveParams: {
        last_page: 'last',
        last_row: 'total',
      },
      dataLoader: false,
      ajaxLoader: true,
    }),
    [handleAjaxResponse, totalCount, queryParams, languageTabulator]
  );

  const { min: xMin, max: xMax } = parseRange(queryParams);

  // Downsample to the chart's pixel width: no point drawing more samples than
  // the chart can show. Measured from one cell (all cells share the grid width).
  const cellRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  useEffect(() => {
    const el = cellRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setChartWidth(Math.round(w / 50) * 50); // 50px steps to limit re-renders
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pointBudget = chartWidth > 0 ? Math.max(100, chartWidth) : 1000;

  const options: ApexOptions = {
    chart: {
      type: 'line',
      zoom: { enabled: false },
      fontFamily: 'Pretendard GOV',
      toolbar: { show: false },
      foreColor: apexColors.foreColor,
    },
    markers: { size: 1 },
    stroke: { show: true, curve: 'straight', width: 2 },
    grid: { borderColor: apexColors.gridBorderColor },
    xaxis: {
      type: 'datetime',
      min: xMin,
      max: xMax,
      labels: { rotate: 0, datetimeUTC: false },
    },
    tooltip: { theme: isDark ? 'dark' : 'light', x: { format: 'yyyy-MM-dd HH:mm' } },
  };

  const ds = (points?: AirMeasurementMetrics[keyof AirMeasurementMetrics]) =>
    lttbGapAware(toSeries(points), pointBudget);

  const chartItems = [
    { id: 'pm10', title: '평균 미세먼지(ug/m³)', data: ds(metrics?.pm10) },
    { id: 'pm25', title: '평균 초미세먼지(ug/m³)', data: ds(metrics?.pm25) },
    { id: 'temp', title: '평균 온도(℃)', data: ds(metrics?.temp) },
    { id: 'humi', title: '평균 습도(%)', data: ds(metrics?.humi) },
    { id: 'co2', title: '평균 이산화탄소(ppm)', data: ds(metrics?.co2) },
    { id: 'vocs', title: '평균 휘발성유기화합물(ppb)', data: ds(metrics?.vocs) },
    { id: 'co', title: '평균 일산화탄소(ppm)', data: ds(metrics?.co) },
    { id: 'hcho', title: '평균 포름알데히드(ppm)', data: ds(metrics?.hcho) },
  ];

  return (
    <section>
      <div className="tabulator-outdoor w-full h-full">
        <ReactTabulator
          columns={IndoorAirTabulator}
          layout={'fitColumns'}
          className="tabulator-custom w-full"
          options={optionsTabulator}
          onRef={(r) => {
            tbRef.current = r.current;
          }}
        />
      </div>

      <section className="flex! flex-col w-full gap-[20px]">
        <div className="_chartAllStatistic mt-[40px] w-full grid! grid-cols-3 gap-[20px]">
          {chartItems.map((chart, idx) => (
            <div
              key={chart.id}
              ref={idx === 0 ? cellRef : null}
              className="border-[3px] rounded-[3px] p-[10px]"
              style={{ borderColor: 'var(--chart-border)' }}
            >
              <span className="title3bold" style={{ color: 'var(--chart-label)' }}>
                {chart.title}
              </span>
              <Chart
                options={{ ...options, colors: [NEWCOLOR.accentGreen] }}
                series={[{ name: chart.title, data: chart.data }]}
                height={244}
                className="flex !min-h-[0]"
                type="line"
              />
            </div>
          ))}
        </div>
      </section>
    </section>
  );
};

export default IndoorAirStatictic;
